import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Heading,
  Grid,
  Spinner,
  Image
} from '@chakra-ui/react';
import { useStats, useSettings } from '../hooks/useApi';

const Home = () => {
  const { data: stats, loading: statsLoading } = useStats();
  const { data: settings } = useSettings();

  return (
    <>
      {/* Hero Section with Image */}
      <Box
        position="relative"
        minH={{ base: "60vh", md: "70vh" }}
        display="flex"
        alignItems="center"
        overflow="hidden"
        bg="black"
      >
        {/* Background Pattern */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.1}
          backgroundImage="radial-gradient(circle at 20% 50%, green.500 1px, transparent 1px), radial-gradient(circle at 80% 30%, green.500 1px, transparent 1px), radial-gradient(circle at 40% 80%, green.500 1px, transparent 1px)"
          backgroundSize="100px 100px, 150px 150px, 80px 80px"
        />

        <Box px={4} w="100%">
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={{ base: 8, lg: 16 }} alignItems="center" maxW="5xl" mx="auto">
            {/* Left Side - Text */}
            <VStack align="start" spacing={8}>
              <VStack align="start" spacing={6}>
                <Heading
                  size={{ base: "xl", md: "2xl", lg: "4xl" }}
                  fontWeight="bold"
                  lineHeight="1.1"
                  letterSpacing="-0.02em"
                  textTransform="uppercase"
                >
                  {settings?.hero_title || "ENGINEERING SYSTEMS THAT HAVE YOU COVERED"}
                </Heading>
                <Box
                  fontSize={{ base: "sm", md: "md", lg: "lg" }}
                  color="gray.300"
                  lineHeight="1.6"
                  maxW={{ base: "100%", lg: "500px" }}
                  dangerouslySetInnerHTML={{
                    __html: settings?.hero_subtitle ||
                      "Building scalable solutions is what we do, and it's what we do best. Web apps, APIs, or infrastructure, our engineering systems deliver thousands of requests, processing every second to scale and perform with precision."
                  }}
                  sx={{
                    '& p': { mb: 4 },
                    '& ul': { pl: 6, mb: 4 },
                    '& ol': { pl: 6, mb: 4 },
                    '& li': { mb: 2 },
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      color: 'white',
                      fontWeight: 'bold',
                      mb: 3,
                      mt: 6
                    },
                    '& strong': { color: 'white' },
                    '& a': { color: 'green.400', textDecoration: 'underline' }
                  }}
                />
              </VStack>
            </VStack>

            {/* Right Side - Hero Image */}
            <Box
              h={{ base: "250px", md: "350px", lg: "400px" }}
              bg="gray.900"
              border="1px solid"
              borderColor="gray.700"
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
              overflow="hidden"
            >
              {settings?.hero_image ? (
                /* CMS Uploaded Image */
                <Image
                  src={settings.hero_image}
                  alt={settings?.hero_title || "Hero Image"}
                  objectFit="cover"
                  w="100%"
                  h="100%"
                />
              ) : (
                /* Fallback Tech Pattern */
                <>
                  {/* Tech Pattern Overlay */}
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    opacity={0.3}
                    backgroundImage="linear-gradient(45deg, transparent 30%, green.900 50%, transparent 70%)"
                  />
                  <Box
                    position="absolute"
                    top="20%"
                    right="20%"
                    w="60px"
                    h="60px"
                    border="2px solid"
                    borderColor="green.500"
                    transform="rotate(45deg)"
                  />
                  <Box
                    position="absolute"
                    bottom="30%"
                    left="25%"
                    w="40px"
                    h="40px"
                    bg="green.500"
                    opacity={0.7}
                    borderRadius="full"
                  />
                  <Text color="gray.400" fontSize="sm" textAlign="center">
                    Engineering Excellence
                    <br />
                    Visualization
                  </Text>
                </>
              )}
            </Box>
          </Grid>
        </Box>
      </Box>

      {/* Solutions Grid - 4 Boxes with Navigation Links */}
      <Box py={{ base: 16, md: 20 }} px={4}>
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6} maxW="5xl" mx="auto">
          {/* Box 1 - Web Development */}
          <Box
            bg="gray.900"
            border="1px solid"
            borderColor="gray.700"
            position="relative"
            _hover={{ bg: "gray.800" }}
            transition="all 0.3s"
            cursor="pointer"
            overflow="hidden"
          >
            <VStack spacing={0} align="stretch">
              {/* Large Image Section */}
              <Box h={{ base: "160px", md: "150px", lg: "200px" }} position="relative" overflow="hidden">
                {settings?.service_1_image ? (
                  <Image
                    src={settings.service_1_image}
                    alt={settings?.service_1_title || "Web Development"}
                    objectFit="cover"
                    objectPosition="center"
                    w="100%"
                    h="100%"
                  />
                ) : (
                  <Box
                    w="100%"
                    h="100%"
                    bg="gray.800"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="sm" color="gray.400" textAlign="center">
                      {settings?.service_1_title || "WEB DEVELOPMENT"}
                    </Text>
                  </Box>
                )}
              </Box>
              
              {/* Content Section */}
              <Box p={{ base: 4, md: 6, lg: 8 }}>
                <VStack align="start" spacing={4}>
                  <Heading size={{ base: "sm", md: "md", lg: "lg" }} textTransform="uppercase" letterSpacing="wide">
                    {settings?.service_1_title || "WEB DEVELOPMENT"}
                  </Heading>
                  <Box
                    color="gray.300"
                    lineHeight="1.6"
                    fontSize={{ base: "xs", md: "sm" }}
                    dangerouslySetInnerHTML={{
                      __html: settings?.service_1_description ||
                        "Give your users seamless experiences and a full view of functionality with our modern web applications. React, Django, and responsive design deliver real-time data to support your business growth."
                    }}
                    sx={{
                      '& p': { mb: 2 },
                      '& ul': { pl: 4, mb: 2 },
                      '& ol': { pl: 4, mb: 2 },
                      '& li': { mb: 1 },
                      '& strong': { color: 'white' },
                      '& a': { color: 'green.400', textDecoration: 'underline' }
                    }}
                  />
                  {settings?.show_service_buttons && (
                    <Link to="/projects">
                      <Button
                        bg="green.500"
                        color="white"
                        size="sm"
                        rightIcon={<Text>→</Text>}
                        _hover={{ bg: "green.600" }}
                      >
                        View Projects
                      </Button>
                    </Link>
                  )}
                </VStack>
              </Box>
            </VStack>
          </Box>

          {/* Box 2 - Cloud Infrastructure */}
          <Box
            bg="gray.900"
            border="1px solid"
            borderColor="gray.700"
            position="relative"
            _hover={{ bg: "gray.800" }}
            transition="all 0.3s"
            cursor="pointer"
            overflow="hidden"
          >
            <VStack spacing={0} align="stretch">
              {/* Large Image Section */}
              <Box h={{ base: "160px", md: "150px", lg: "200px" }} position="relative" overflow="hidden">
                {settings?.service_2_image ? (
                  <Image
                    src={settings.service_2_image}
                    alt={settings?.service_2_title || "Cloud Infrastructure"}
                    objectFit="cover"
                    objectPosition="center"
                    w="100%"
                    h="100%"
                  />
                ) : (
                  <Box
                    w="100%"
                    h="100%"
                    bg="gray.800"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="sm" color="gray.400" textAlign="center">
                      {settings?.service_2_title || "CLOUD INFRASTRUCTURE"}
                    </Text>
                  </Box>
                )}
              </Box>
              
              {/* Content Section */}
              <Box p={{ base: 4, md: 6, lg: 8 }}>
                <VStack align="start" spacing={4}>
                  <Heading size={{ base: "sm", md: "md", lg: "lg" }} textTransform="uppercase" letterSpacing="wide">
                    {settings?.service_2_title || "CLOUD INFRASTRUCTURE"}
                  </Heading>
                  <Box
                    color="gray.300"
                    lineHeight="1.6"
                    fontSize={{ base: "xs", md: "sm" }}
                    dangerouslySetInnerHTML={{
                      __html: settings?.service_2_description ||
                        "Our cloud systems deploy and integrate in minutes, alerting you of performance metrics and scaling as early as possible. Detect, monitor and optimize infrastructure in real-time for informed, efficient operations."
                    }}
                    sx={{
                      '& p': { mb: 2 },
                      '& ul': { pl: 4, mb: 2 },
                      '& ol': { pl: 4, mb: 2 },
                      '& li': { mb: 1 },
                      '& strong': { color: 'white' },
                      '& a': { color: 'green.400', textDecoration: 'underline' }
                    }}
                  />
                  {settings?.show_service_buttons && (
                    <Link to="/projects">
                      <Button
                        bg="green.500"
                        color="white"
                        size="sm"
                        rightIcon={<Text>→</Text>}
                        _hover={{ bg: "green.600" }}
                      >
                        View Infrastructure
                      </Button>
                    </Link>
                  )}
                </VStack>
              </Box>
            </VStack>
          </Box>

          {/* Box 3 - Technical Articles */}
          <Box
            bg="gray.900"
            border="1px solid"
            borderColor="gray.700"
            position="relative"
            _hover={{ bg: "gray.800" }}
            transition="all 0.3s"
            cursor="pointer"
            overflow="hidden"
          >
            <VStack spacing={0} align="stretch">
              {/* Large Image Section */}
              <Box h={{ base: "160px", md: "150px", lg: "200px" }} position="relative" overflow="hidden">
                {settings?.service_3_image ? (
                  <Image
                    src={settings.service_3_image}
                    alt={settings?.service_3_title || "Technical Articles"}
                    objectFit="cover"
                    objectPosition="center"
                    w="100%"
                    h="100%"
                  />
                ) : (
                  <Box
                    w="100%"
                    h="100%"
                    bg="gray.800"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="sm" color="gray.400" textAlign="center">
                      {settings?.service_3_title || "TECHNICAL ARTICLES"}
                    </Text>
                  </Box>
                )}
              </Box>
              
              {/* Content Section */}
              <Box p={{ base: 4, md: 6, lg: 8 }}>
                <VStack align="start" spacing={4}>
                  <Heading size={{ base: "sm", md: "md", lg: "lg" }} textTransform="uppercase" letterSpacing="wide">
                    {settings?.service_3_title || "TECHNICAL ARTICLES"}
                  </Heading>
                  <Box
                    color="gray.300"
                    lineHeight="1.6"
                    fontSize={{ base: "xs", md: "sm" }}
                    dangerouslySetInnerHTML={{
                      __html: settings?.service_3_description ||
                        "Deep dives into engineering challenges, best practices, and lessons learned. From API design to deployment strategies, we share insights from real-world projects and cutting-edge development techniques."
                    }}
                    sx={{
                      '& p': { mb: 2 },
                      '& ul': { pl: 4, mb: 2 },
                      '& ol': { pl: 4, mb: 2 },
                      '& li': { mb: 1 },
                      '& strong': { color: 'white' },
                      '& a': { color: 'green.400', textDecoration: 'underline' }
                    }}
                  />
                  {settings?.show_service_buttons && (
                    <Link to="/articles">
                      <Button
                        bg="green.500"
                        color="white"
                        size="sm"
                        rightIcon={<Text>→</Text>}
                        _hover={{ bg: "green.600" }}
                      >
                        Read Articles
                      </Button>
                    </Link>
                  )}
                </VStack>
              </Box>
            </VStack>
          </Box>

          {/* Box 4 - Project Portfolio */}
          <Box
            bg="gray.900"
            border="1px solid"
            borderColor="gray.700"
            position="relative"
            _hover={{ bg: "gray.800" }}
            transition="all 0.3s"
            cursor="pointer"
            overflow="hidden"
          >
            <VStack spacing={0} align="stretch">
              {/* Large Image Section */}
              <Box h={{ base: "160px", md: "150px", lg: "200px" }} position="relative" overflow="hidden">
                {settings?.service_4_image ? (
                  <Image
                    src={settings.service_4_image}
                    alt={settings?.service_4_title || "Project Portfolio"}
                    objectFit="cover"
                    objectPosition="center"
                    w="100%"
                    h="100%"
                  />
                ) : (
                  <Box
                    w="100%"
                    h="100%"
                    bg="gray.800"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="sm" color="gray.400" textAlign="center">
                      {settings?.service_4_title || "PROJECT PORTFOLIO"}
                    </Text>
                  </Box>
                )}
              </Box>
              
              {/* Content Section */}
              <Box p={{ base: 4, md: 6, lg: 8 }}>
                <VStack align="start" spacing={4}>
                  <Heading size={{ base: "sm", md: "md", lg: "lg" }} textTransform="uppercase" letterSpacing="wide">
                    {settings?.service_4_title || "PROJECT PORTFOLIO"}
                  </Heading>
                  <Box
                    color="gray.300"
                    lineHeight="1.6"
                    fontSize={{ base: "xs", md: "sm" }}
                    dangerouslySetInnerHTML={{
                      __html: settings?.service_4_description ||
                        "Explore our completed projects and case studies. From e-commerce platforms to enterprise APIs, see how we've helped teams build scalable, reliable systems that drive business results."
                    }}
                    sx={{
                      '& p': { mb: 2 },
                      '& ul': { pl: 4, mb: 2 },
                      '& ol': { pl: 4, mb: 2 },
                      '& li': { mb: 1 },
                      '& strong': { color: 'white' },
                      '& a': { color: 'green.400', textDecoration: 'underline' }
                    }}
                  />
                  {settings?.show_service_buttons && (
                    <Link to="/projects">
                      <Button
                        bg="green.500"
                        color="white"
                        size="sm"
                        rightIcon={<Text>→</Text>}
                        _hover={{ bg: "green.600" }}
                      >
                        View Portfolio
                      </Button>
                    </Link>
                  )}
                </VStack>
              </Box>
            </VStack>
          </Box>
        </Grid>
      </Box>

      {/* Why Choose Us - Dynamic Stats Section */}
      <Box py={{ base: 16, md: 24 }} px={4}>
        <VStack spacing={{ base: 12, md: 20 }}>
          <Heading size={{ base: "lg", md: "xl", lg: "3xl" }} textTransform="uppercase" letterSpacing="wide" textAlign="center" w="100%" maxW="5xl" mx="auto">
            {settings?.stats_section_title || "WHY CLOVER RIDGE TECHNOLOGIES?"}
          </Heading>

          {statsLoading ? (
            <VStack spacing={4}>
              <Spinner size="lg" color="green.500" />
              <Text color="gray.300">Loading statistics...</Text>
            </VStack>
          ) : (
            <Grid 
              templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} 
              gap={{ base: 8, md: 6, lg: 8 }} 
              maxW="6xl" 
              mx="auto"
              px={4}
            >
              {/* Stat 1 - Years of Experience */}
              <VStack align="start" spacing={4}>
                <HStack>
                  <Box w={3} h={3} bg="green.500" borderRadius="full" />
                  <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} color="white" fontWeight="bold">
                    {stats?.years_experience || settings?.stat_1_number || "5+"}
                  </Heading>
                </HStack>
                <VStack align="start" spacing={2}>
                  <Text fontSize={{ base: "sm", md: "md", lg: "lg" }} fontWeight="bold" textTransform="uppercase" letterSpacing="wide">
                    {settings?.stat_1_title || "YEARS OF CODE CRAFT"}
                  </Text>
                  <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }} lineHeight="1.6">
                    {settings?.stat_1_description ||
                     "Our engineering heritage is steeped in software science. A combination of experience, innovation, and a fierce drive to build solutions for modern problems make us a reliable technology partner."
                    }
                  </Text>
                </VStack>
              </VStack>

              {/* Stat 2 - Uptime */}
              <VStack align="start" spacing={4}>
                <HStack>
                  <Box w={3} h={3} bg="green.500" borderRadius="full" />
                  <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} color="white" fontWeight="bold">
                    {stats?.uptime_percentage || settings?.stat_2_number || "100%"}
                  </Heading>
                </HStack>
                <VStack align="start" spacing={2}>
                  <Text fontSize={{ base: "sm", md: "md", lg: "lg" }} fontWeight="bold" textTransform="uppercase" letterSpacing="wide">
                    {settings?.stat_2_title || "UPTIME GUARANTEED"}
                  </Text>
                  <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }} lineHeight="1.6">
                    {settings?.stat_2_description ||
                     "Most systems monitor in one direction or provide partial coverage in production. Our systems combine reliable infrastructure in one solution for full awareness of what's happening all around you, all year round."
                    }
                  </Text>
                </VStack>
              </VStack>

              {/* Stat 3 - Projects Delivered */}
              <VStack align="start" spacing={4}>
                <HStack>
                  <Box w={3} h={3} bg="green.500" borderRadius="full" />
                  <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} color="white" fontWeight="bold">
                    {stats?.total_projects || settings?.stat_3_number || "50+"}
                  </Heading>
                </HStack>
                <VStack align="start" spacing={2}>
                  <Text fontSize={{ base: "sm", md: "md", lg: "lg" }} fontWeight="bold" textTransform="uppercase" letterSpacing="wide">
                    {settings?.stat_3_title || "PROJECTS DELIVERED"}
                  </Text>
                  <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }} lineHeight="1.6">
                    {settings?.stat_3_description ||
                     "We focus on development as a crucial piece of a full solution. Our systems slot seamlessly into your tech stack, with an interoperable interface that adapts to your needs and setup."
                    }
                  </Text>
                </VStack>
              </VStack>

              {/* Stat 4 - Support */}
              <VStack align="start" spacing={4}>
                <HStack>
                  <Box w={3} h={3} bg="green.500" borderRadius="full" />
                  <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} color="white" fontWeight="bold">
                    {settings?.stat_4_number || "24/7"}
                  </Heading>
                </HStack>
                <VStack align="start" spacing={2}>
                  <Text fontSize={{ base: "sm", md: "md", lg: "lg" }} fontWeight="bold" textTransform="uppercase" letterSpacing="wide">
                    {settings?.stat_4_title || "SUPPORT AVAILABLE"}
                  </Text>
                  <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }} lineHeight="1.6">
                    {settings?.stat_4_description ||
                     "We're proud of our engineering, and prouder still of our legacy of innovation. We write, test, and deploy everything we build. Meticulous development keeps our solutions at the cutting edge."
                    }
                  </Text>
                </VStack>
              </VStack>
            </Grid>
          )}
        </VStack>
      </Box>

      {/* Our Technologies - React/Django */}
      <Box py={{ base: 16, md: 24 }} px={4} textAlign="center">
        <VStack spacing={{ base: 12, md: 16 }}>
          <VStack spacing={4}>
            <Heading size={{ base: "lg", md: "xl", lg: "3xl" }} textTransform="uppercase" letterSpacing="wide">
              {settings?.tech_section_title || "OUR TECHNOLOGIES"}
            </Heading>
            <Box
              fontSize={{ base: "sm", md: "md", lg: "lg" }}
              color="gray.300"
              maxW="4xl"
              dangerouslySetInnerHTML={{
                __html: settings?.tech_section_description ||
                  "Frontend, backend, or infrastructure, when it comes to modern development, our specialized engineering solutions are a cut above the rest."
              }}
              sx={{
                '& p': { mb: 2 },
                '& ul': { pl: 4, mb: 2 },
                '& ol': { pl: 4, mb: 2 },
                '& li': { mb: 1 },
                '& strong': { color: 'white' },
                '& a': { color: 'green.400', textDecoration: 'underline' }
              }}
            />
          </VStack>

          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={0} maxW="5xl" mx="auto">
            {/* Tech 1 - React */}
            <Box p={{ base: 6, md: 8, lg: 12 }} border="1px solid" borderColor="gray.800" bg="gray.900">
              <VStack spacing={6}>
                <Heading size={{ base: "lg", md: "xl", lg: "2xl" }} fontWeight="bold" letterSpacing="wide">
                  {settings?.tech_1_name || "REACT"}
                </Heading>
                <Box
                  color="gray.300"
                  textAlign="center"
                  lineHeight="1.6"
                  fontSize={{ base: "xs", md: "sm" }}
                  dangerouslySetInnerHTML={{
                    __html: settings?.tech_1_description ||
                      "Modern frontend framework, React has lightning-fast rendering speed and extensive component capability."
                  }}
                  sx={{
                    '& p': { mb: 2 },
                    '& ul': { pl: 4, mb: 2, textAlign: 'left' },
                    '& ol': { pl: 4, mb: 2, textAlign: 'left' },
                    '& li': { mb: 1 },
                    '& strong': { color: 'white' },
                    '& a': { color: 'green.400', textDecoration: 'underline' }
                  }}
                />
              </VStack>
            </Box>

            {/* Tech 2 - Django */}
            <Box p={{ base: 6, md: 8, lg: 12 }} border="1px solid" borderColor="gray.800" bg="gray.900">
              <VStack spacing={6}>
                <Heading size={{ base: "lg", md: "xl", lg: "2xl" }} fontWeight="bold" letterSpacing="wide">
                  {settings?.tech_2_name || "DJANGO"}
                </Heading>
                <Box
                  color="gray.300"
                  textAlign="center"
                  lineHeight="1.6"
                  fontSize={{ base: "xs", md: "sm" }}
                  dangerouslySetInnerHTML={{
                    __html: settings?.tech_2_description ||
                      "Django offers robust backends, scalability, and enterprise-grade security in a powerful but lightweight framework."
                  }}
                  sx={{
                    '& p': { mb: 2 },
                    '& ul': { pl: 4, mb: 2, textAlign: 'left' },
                    '& ol': { pl: 4, mb: 2, textAlign: 'left' },
                    '& li': { mb: 1 },
                    '& strong': { color: 'white' },
                    '& a': { color: 'green.400', textDecoration: 'underline' }
                  }}
                />
              </VStack>
            </Box>

            {/* Tech 3 - Web Apps */}
            <Box p={{ base: 6, md: 8, lg: 12 }} border="1px solid" borderColor="gray.800" bg="gray.900">
              <VStack spacing={6}>
                <Heading size={{ base: "lg", md: "xl", lg: "2xl" }} fontWeight="bold" letterSpacing="wide">
                  {settings?.tech_3_name || "WEB APPS"}
                </Heading>
                <Box
                  color="gray.300"
                  textAlign="center"
                  lineHeight="1.6"
                  fontSize={{ base: "xs", md: "sm" }}
                  dangerouslySetInnerHTML={{
                    __html: settings?.tech_3_description ||
                      "Full-stack web applications with modern frameworks, responsive design, and seamless user experiences that drive business growth."
                  }}
                  sx={{
                    '& p': { mb: 2 },
                    '& ul': { pl: 4, mb: 2, textAlign: 'left' },
                    '& ol': { pl: 4, mb: 2, textAlign: 'left' },
                    '& li': { mb: 1 },
                    '& strong': { color: 'white' },
                    '& a': { color: 'green.400', textDecoration: 'underline' }
                  }}
                />
              </VStack>
            </Box>

            {/* Tech 4 - Mobile Apps */}
            <Box p={{ base: 6, md: 8, lg: 12 }} border="1px solid" borderColor="gray.800" bg="gray.900">
              <VStack spacing={6}>
                <Heading size={{ base: "lg", md: "xl", lg: "2xl" }} fontWeight="bold" letterSpacing="wide">
                  {settings?.tech_4_name || "MOBILE APPS"}
                </Heading>
                <Box
                  color="gray.300"
                  textAlign="center"
                  lineHeight="1.6"
                  fontSize={{ base: "xs", md: "sm" }}
                  dangerouslySetInnerHTML={{
                    __html: settings?.tech_4_description ||
                      "Native and cross-platform mobile applications for iOS and Android with intuitive interfaces and optimized performance."
                  }}
                  sx={{
                    '& p': { mb: 2 },
                    '& ul': { pl: 4, mb: 2, textAlign: 'left' },
                    '& ol': { pl: 4, mb: 2, textAlign: 'left' },
                    '& li': { mb: 1 },
                    '& strong': { color: 'white' },
                    '& a': { color: 'green.400', textDecoration: 'underline' }
                  }}
                />
              </VStack>
            </Box>
          </Grid>
        </VStack>
      </Box>

      {/* Markets We Serve */}
      <Box py={{ base: 16, md: 24 }} px={4}>
        <VStack spacing={{ base: 12, md: 16 }}>
          <VStack spacing={6} textAlign="left" w="100%" maxW="5xl" mx="auto">
            <Heading size={{ base: "lg", md: "xl", lg: "3xl" }} textTransform="uppercase" letterSpacing="wide">
              {settings?.markets_section_title || "MARKETS WE SERVE"}
            </Heading>
            <Box
              fontSize={{ base: "sm", md: "md", lg: "lg" }}
              color="gray.300"
              maxW="3xl"
              dangerouslySetInnerHTML={{
                __html: settings?.markets_section_description ||
                  "Our engineering systems deliver high-quality solutions, rapid development, and the best possible performance to scale and adapt as requirements emerge."
              }}
              sx={{
                '& p': { mb: 2 },
                '& ul': { pl: 4, mb: 2 },
                '& ol': { pl: 4, mb: 2 },
                '& li': { mb: 1 },
                '& strong': { color: 'white' },
                '& a': { color: 'green.400', textDecoration: 'underline' }
              }}
            />
          </VStack>

          <Grid templateColumns={{ base: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(6, 1fr)" }} gap={0} maxW="5xl" mx="auto">
            {/* Market Icons */}
            <VStack spacing={4} p={{ base: 3, md: 6, lg: 8 }} borderRight={{ base: "1px solid", md: "1px solid" }} borderBottom={{ base: "1px solid", md: "none" }} borderColor="gray.800">
              <Box fontSize={{ base: "lg", md: "2xl" }}>🏢</Box>
              <HStack flexWrap="wrap" justify="center">
                <Box w={2} h={2} bg="green.500" borderRadius="full" />
                <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="bold" color="white" textTransform="uppercase" letterSpacing="wide" textAlign="center">
                  STARTUPS
                </Text>
              </HStack>
            </VStack>

            <VStack spacing={4} p={{ base: 3, md: 6, lg: 8 }} borderRight={{ base: "none", md: "1px solid" }} borderBottom={{ base: "1px solid", md: "none" }} borderColor="gray.800">
              <Box fontSize={{ base: "lg", md: "2xl" }}>🏭</Box>
              <HStack flexWrap="wrap" justify="center">
                <Box w={2} h={2} bg="green.500" borderRadius="full" />
                <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="bold" color="white" textTransform="uppercase" letterSpacing="wide" textAlign="center">
                  ENTERPRISE
                </Text>
              </HStack>
            </VStack>

            <VStack spacing={4} p={{ base: 3, md: 6, lg: 8 }} borderRight={{ base: "1px solid", md: "1px solid" }} borderBottom={{ base: "1px solid", md: "none" }} borderColor="gray.800">
              <Box fontSize={{ base: "lg", md: "2xl" }}>💰</Box>
              <HStack flexWrap="wrap" justify="center">
                <Box w={2} h={2} bg="green.500" borderRadius="full" />
                <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="bold" color="white" textTransform="uppercase" letterSpacing="wide" textAlign="center">
                  FINTECH
                </Text>
              </HStack>
            </VStack>

            <VStack spacing={4} p={{ base: 3, md: 6, lg: 8 }} borderRight={{ base: "none", md: "1px solid" }} borderBottom={{ base: "1px solid", md: "none" }} borderColor="gray.800">
              <Box fontSize={{ base: "lg", md: "2xl" }}>🛒</Box>
              <HStack flexWrap="wrap" justify="center">
                <Box w={2} h={2} bg="green.500" borderRadius="full" />
                <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="bold" color="white" textTransform="uppercase" letterSpacing="wide" textAlign="center">
                  E-COMMERCE
                </Text>
              </HStack>
            </VStack>

            <VStack spacing={4} p={{ base: 3, md: 6, lg: 8 }} borderRight={{ base: "1px solid", md: "1px solid" }} borderColor="gray.800">
              <Box fontSize={{ base: "lg", md: "2xl" }}>🏥</Box>
              <HStack flexWrap="wrap" justify="center">
                <Box w={2} h={2} bg="green.500" borderRadius="full" />
                <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="bold" color="white" textTransform="uppercase" letterSpacing="wide" textAlign="center">
                  HEALTHCARE
                </Text>
              </HStack>
            </VStack>

            <VStack spacing={4} p={{ base: 3, md: 6, lg: 8 }}>
              <Box fontSize={{ base: "lg", md: "2xl" }}>🎓</Box>
              <HStack flexWrap="wrap" justify="center">
                <Box w={2} h={2} bg="green.500" borderRadius="full" />
                <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="bold" color="white" textTransform="uppercase" letterSpacing="wide" textAlign="center">
                  EDUCATION
                </Text>
              </HStack>
            </VStack>
          </Grid>

          {/* Testimonial */}
          {settings?.show_testimonial && (
            <Box maxW="4xl" mx="auto" py={{ base: 12, md: 16 }}>
              <VStack spacing={8}>
                <Text fontSize={{ base: "md", md: "lg", lg: "2xl" }} color="white" lineHeight="1.4" textAlign="center" fontWeight="medium">
                  {settings?.testimonial_quote ||
                   '"I\'m convinced modern web applications will become a self-evident part of next generation digital transformation policies at larger companies. In part, because it allows us to objectively measure and use performance as a key indicator."'
                  }
                </Text>
                <VStack spacing={2}>
                  <Text fontSize={{ base: "md", md: "lg", lg: "xl" }} fontWeight="bold" color="white">
                    {settings?.testimonial_author || "ALEX THOMPSON"}
                  </Text>
                  <Text color="green.500" fontSize="sm">
                    {settings?.testimonial_title || "CTO, TechStart Solutions"}
                  </Text>
                  <Link to="/projects">
                    <Text color="gray.400" fontSize="sm" textDecoration="underline" cursor="pointer" _hover={{ color: "green.500" }}>
                      Explore our web development solutions →
                    </Text>
                  </Link>
                </VStack>
              </VStack>
            </Box>
          )}
        </VStack>
      </Box>
    </>
  );
};

export default Home;